import { ComponentProps, useEffect } from 'react'

import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { ErrorRouteComponent, useRouter } from '@tanstack/react-router'
import i18next, { TFunction } from 'i18next'

import { Alert, Button, IconName, ListItem, Tooltip, TooltipContent, TooltipTrigger } from '@forto-dev/phoenix-ui-core'
import { Stack } from '@forto-dev/phoenix-ui-core/common'
import { Splash } from '@forto-dev/phoenix-ui-core/ship'

import { useLocalisation } from '@/utils/localisation'

/**
 * from https://tanstack.com/router/latest/docs/framework/react/guide/external-data-loading#error-handling-with-tanstack-query
 */

// Detects the browser error thrown when a lazy-loaded chunk fails to fetch.
// Covers Chrome, Firefox, and Safari error message variants.
export const isChunkLoadError = (error: Error): boolean => {
  const msg = error instanceof Error ? error.message : String(error)
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed')
  )
}

type AppFallbackTranslationKey =
  | 'errors.chunkLoad.title'
  | 'errors.chunkLoad.description'
  | 'errors.offline.title'
  | 'errors.offline.description'
  | 'actions.reload'

// i18n context/init may be unavailable when the shell itself failed to load.
const safeTranslate = (key: AppFallbackTranslationKey, fallback: string): string => {
  try {
    return i18next.isInitialized && i18next.exists(key) ? i18next.t(key) : fallback
  } catch {
    return fallback
  }
}

type ErrorHandlerContext = {
  message?: string
  error: Error
  translate: TFunction
  reset: () => void
  invalidate: () => void
}

type AlertVariant = ComponentProps<typeof Alert>['variant']

type ErrorHandlerProps = {
  title: string
  description: string
  actionLabel: string
  onAction: () => void
  variant: AlertVariant
}

type ErrorHandler = {
  matches: (error: Error) => boolean
  build: (ctx: ErrorHandlerContext) => ErrorHandlerProps
}

const errorHandlers: ErrorHandler[] = [
  {
    matches: (error) => isChunkLoadError(error) && navigator.onLine,
    build: ({ translate }) => ({
      title: translate('errors.chunkLoad.title'),
      description: translate('errors.chunkLoad.description'),
      actionLabel: translate('actions.reload'),
      onAction: () => window.location.reload(),
      variant: 'negative',
    }),
  },
  {
    matches: (error) => isChunkLoadError(error) && !navigator.onLine,
    build: ({ translate, reset, invalidate }) => ({
      title: translate('errors.offline.title'),
      description: translate('errors.offline.description'),
      actionLabel: translate('actions.retry'),
      onAction: () => {
        reset()
        invalidate()
      },
      variant: 'muted',
    }),
  },
]

const resolveErrorProps = (ctx: ErrorHandlerContext): ErrorHandlerProps =>
  errorHandlers.find((h) => h.matches(ctx.error))?.build(ctx) ?? {
    title: ctx.message ?? ctx.error.message,
    description: ctx.error.message,
    actionLabel: ctx.translate('actions.retry'),
    onAction: () => {
      // Reset the router error boundary
      ctx.reset()
      // Invalidate the route to reload the loader
      ctx.invalidate()
    },
    variant: 'negative',
  }

export const createShipmentContextErrorFallback: ({
  icon,
  testId,
  message,
}: {
  icon: IconName
  testId?: string
  message: string
}) => ErrorRouteComponent =
  ({ icon, testId, message }) =>
  ({ error }) => {
    const queryErrorResetBoundary = useQueryErrorResetBoundary()

    useEffect(() => {
      queryErrorResetBoundary.reset()
    }, [queryErrorResetBoundary])

    return (
      <Tooltip>
        <TooltipTrigger>
          <ListItem icon={icon} disabled data-testid={testId}>
            {message}
          </ListItem>
        </TooltipTrigger>
        <TooltipContent>{error.message}</TooltipContent>
      </Tooltip>
    )
  }

export const createErrorFallback: (message: string) => ErrorRouteComponent =
  (message: string) =>
  ({ error, reset }) => {
    const router = useRouter()
    const queryErrorResetBoundary = useQueryErrorResetBoundary()
    const { translate } = useLocalisation()

    useEffect(() => {
      // Reset the query error boundary
      queryErrorResetBoundary.reset()
    }, [queryErrorResetBoundary])

    const { title, description, actionLabel, onAction, variant } = resolveErrorProps({
      error,
      translate,
      reset,
      invalidate: router.invalidate,
      message,
    })

    return (
      <Stack gap={16}>
        <Alert variant={variant} title={title} description={description} />
        <Button onClick={onAction}>{actionLabel}</Button>
      </Stack>
    )
  }

const resolveAppErrorTexts = (error: Error): { title: string; description: string; variant: AlertVariant } => {
  if (!isChunkLoadError(error)) {
    return { title: error.message, description: error.message, variant: 'negative' }
  }
  if (!navigator.onLine) {
    return {
      title: safeTranslate('errors.offline.title', "You're offline"),
      description: safeTranslate('errors.offline.description', 'Check your internet connection and try again.'),
      variant: 'muted',
    }
  }
  return {
    title: safeTranslate('errors.chunkLoad.title', 'Failed to load page'),
    description: safeTranslate(
      'errors.chunkLoad.description',
      'Something went wrong loading this page. Try reloading.'
    ),
    variant: 'negative',
  }
}

// Renders outside all providers (app-level CatchBoundary): no provider-dependent
// hooks — a throw here has no boundary left and unmounts the app.
export const createAppErrorFallback: () => ErrorRouteComponent =
  () =>
  ({ error }) => {
    const { title, description, variant } = resolveAppErrorTexts(error)

    return (
      <Splash>
        <Stack gap={16} x="center">
          <Alert variant={variant} title={title} description={description} />
          <Button onClick={() => window.location.reload()}>{safeTranslate('actions.reload', 'Reload')}</Button>
        </Stack>
      </Splash>
    )
  }
